import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Camera,
  CheckCircle,
  Heart,
  PaperPlaneTilt,
  Phone,
  User,
  WarningCircle,
} from '@phosphor-icons/react'
import KioskHeader from '../../components/KioskHeader/KioskHeader'
import useKioskAutoHome from '../../hooks/useKioskAutoHome'
import { useLang } from '../../context/LangContext'

const CONTENT = {
  en: {
    pageTitle: 'Say Hi',
    pageSubtitle: 'Share your message for the community leader',

    title: 'Say Hi',
    subtitle:
      'Visitors can post a message and capture a photo to support the community leader.',

    name: 'Name',
    phone: 'Phone',
    message: 'Message (optional)',
    image: 'Image',

    namePlaceholder: 'Your name',
    phonePlaceholder: '161234567',
    messagePlaceholder: 'Write your support message here...',

    liveCameraRequired: 'Live camera required',
    liveCameraDesc: 'Please capture a photo before submitting.',
    openCamera: 'Open Camera',
    capturePhoto: 'Capture Photo',
    capturingIn: 'Capturing in',
    getReady: 'Get ready',
    retakePhoto: 'Retake Photo',

    cameraError:
      'Unable to open the camera. Please allow camera permission and try again.',

    disclaimer:
      'By submitting this form, you allow us to use your message and captured image for social media, online media, publicity, and community-related communication.',

    backHome: 'Back to Home',
    submit: 'Submit Message',

    successTitle: 'Thank you for your support.',
    successDesc:
      'Your message has been submitted. The kiosk will return to the home page shortly.',

    phoneRequired: 'Phone number is required',
    invalidPhone: 'Please enter a valid Malaysia mobile number',
  },

  bm: {
    pageTitle: 'Say Hi',
    pageSubtitle: 'Kongsi mesej anda untuk pemimpin komuniti',

    title: 'Say Hi',
    subtitle:
      'Pelawat boleh menghantar mesej dan mengambil gambar untuk menyokong pemimpin komuniti.',

    name: 'Nama',
    phone: 'Telefon',
    message: 'Mesej (pilihan)',
    image: 'Gambar',

    namePlaceholder: 'Nama anda',
    phonePlaceholder: '161234567',
    messagePlaceholder: 'Tulis mesej sokongan anda di sini...',

    liveCameraRequired: 'Kamera langsung diperlukan',
    liveCameraDesc: 'Sila ambil gambar sebelum menghantar.',
    openCamera: 'Buka Kamera',
    capturePhoto: 'Ambil Gambar',
    capturingIn: 'Mengambil dalam',
    getReady: 'Bersedia',
    retakePhoto: 'Ambil Semula',

    cameraError:
      'Kamera tidak dapat dibuka. Sila benarkan akses kamera dan cuba lagi.',

    disclaimer:
      'Dengan menghantar borang ini, anda membenarkan kami menggunakan mesej dan gambar anda untuk media sosial, media dalam talian, publisiti, dan komunikasi berkaitan komuniti.',

    backHome: 'Kembali ke Halaman Utama',
    submit: 'Hantar Mesej',

    successTitle: 'Terima kasih atas sokongan anda.',
    successDesc:
      'Mesej anda telah dihantar. Kiosk akan kembali ke halaman utama sebentar lagi.',

    phoneRequired: 'Nombor telefon diperlukan',
    invalidPhone: 'Sila masukkan nombor telefon Malaysia yang sah',
  },
}

export default function SupportUsPage() {
  useKioskAutoHome()

  const navigate = useNavigate()
  const { lang } = useLang()
  const t = CONTENT[lang] || CONTENT.en

  const videoRef = useRef(null)
  const canvasRef = useRef(null)
  const streamRef = useRef(null)

  const [name, setName] = useState('')
  const [phone, setPhone] = useState('')
  const [message, setMessage] = useState('')
  const [photo, setPhoto] = useState(null)
  const [phoneError, setPhoneError] = useState('')
  const [cameraActive, setCameraActive] = useState(false)
  const [cameraError, setCameraError] = useState('')
  const [submitted, setSubmitted] = useState(false)
  const [captureCountdown, setCaptureCountdown] = useState(0)

  const isPhoneValid = /^1\d{8,9}$/.test(phone)

  const canSubmit =
    name.trim().length > 0 &&
    isPhoneValid &&
    Boolean(photo) &&
    !phoneError

  const handlePhoneChange = (e) => {
    let value = e.target.value.replace(/\D/g, '')

    if (value.startsWith('60')) value = value.slice(2)
    if (value.startsWith('0')) value = value.slice(1)
    if (value.length > 10) value = value.slice(0, 10)

    setPhone(value)

    if (!value) {
      setPhoneError(t.phoneRequired)
    } else if (!/^1\d{8,9}$/.test(value)) {
      setPhoneError(t.invalidPhone)
    } else {
      setPhoneError('')
    }
  }

  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop())
      streamRef.current = null
    }

    setCameraActive(false)
  }

  const startCamera = async () => {
    setCameraError('')

    try {
      stopCamera()

      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: 'user',
          width: { ideal: 1280 },
          height: { ideal: 720 },
        },
        audio: false,
      })

      streamRef.current = stream

      if (videoRef.current) {
        videoRef.current.srcObject = stream
        await videoRef.current.play()
      }

      setCameraActive(true)
    } catch (error) {
      console.error('Unable to open camera:', error)
      setCameraError(t.cameraError)
      setCameraActive(false)
    }
  }

  const startCaptureCountdown = () => {
    if (!cameraActive || captureCountdown > 0) return

    setCaptureCountdown(3)

    let nextCount = 3

    const timer = window.setInterval(() => {
      nextCount -= 1

      if (nextCount <= 0) {
        window.clearInterval(timer)
        setCaptureCountdown(0)
        capturePhoto()
        return
      }

      setCaptureCountdown(nextCount)
    }, 1000)
  }

  const capturePhoto = () => {
    const video = videoRef.current
    const canvas = canvasRef.current

    if (!video || !canvas) return

    const width = video.videoWidth || 1280
    const height = video.videoHeight || 720

    canvas.width = width
    canvas.height = height

    const context = canvas.getContext('2d')

    context.translate(width, 0)
    context.scale(-1, 1)
    context.drawImage(video, 0, 0, width, height)

    const imageData = canvas.toDataURL('image/jpeg', 0.9)

    setPhoto(imageData)
    stopCamera()
  }

  const retakePhoto = () => {
    setCaptureCountdown(0)
    setPhoto(null)
    startCamera()
  }

  const handleSubmit = (e) => {
    e.preventDefault()

    if (!canSubmit) return

    const fullPhoneNumber = `+60${phone}`

    // TODO: connect to backend API
    // Send: name, fullPhoneNumber, message, photo, media consent

    console.log({
      name,
      phone: fullPhoneNumber,
      message,
      photo,
      media_consent: true,
    })

    stopCamera()
    setSubmitted(true)

    window.setTimeout(() => {
      navigate('/', { replace: true })
    }, 3500)
  }

  useEffect(() => {
    return () => stopCamera()
  }, [])

  return (
    <main className="kiosk-page">
      <div className="app-container kiosk-container">
        <KioskHeader
          title={t.pageTitle}
          subtitle={t.pageSubtitle}
        />

        <section className="kiosk-section-card support-us-card">
          {!submitted ? (
            <form onSubmit={handleSubmit}>
              <div className="kiosk-section-card__header">
                <Heart size={30} />

                <div>
                  <h1>{t.title}</h1>
                  <p>{t.subtitle}</p>
                </div>
              </div>

              <div className="form-grid support-us-form-grid">
                <div className="form-group">
                  <label className="form-label">
                    <User size={15} /> {t.name} <strong>*</strong>
                  </label>

                  <input
                    className="form-input"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder={t.namePlaceholder}
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">
                    <Phone size={15} /> {t.phone} <strong>*</strong>
                  </label>

                  <div className="phone-input phone-input--malaysia">
                    <div className="phone-input__prefix">🇲🇾 +60</div>

                    <input
                      type="tel"
                      inputMode="numeric"
                      value={phone}
                      onChange={handlePhoneChange}
                      onBlur={handlePhoneChange}
                      className={`phone-input__number ${
                        phoneError ? 'is-error' : ''
                      }`}
                      placeholder={t.phonePlaceholder}
                    />
                  </div>

                  {phoneError && (
                    <div className="form-error">
                      {phoneError}
                    </div>
                  )}
                </div>

                <div className="form-group form-group--full">
                  <label className="form-label">
                    {t.message}
                  </label>

                  <textarea
                    className="form-textarea"
                    rows={6}
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    placeholder={t.messagePlaceholder}
                  />
                </div>

                <div className="form-group form-group--full">
                  <label className="form-label">
                    {t.image} <strong>*</strong>
                  </label>

                  <div className={`support-us-live-camera ${photo ? 'has-photo' : ''}`}>
                    {!photo && (
                      <>
                        <video
                          ref={videoRef}
                          className={`support-us-live-camera__video ${
                            cameraActive ? 'is-active' : ''
                          }`}
                          playsInline
                          muted
                          autoPlay
                        />

                        {!cameraActive && (
                          <div className="support-us-live-camera__empty">
                            <Camera size={44} />
                            <strong>{t.liveCameraRequired}</strong>
                            <span>{t.liveCameraDesc}</span>
                          </div>
                        )}
                      </>
                    )}

                    {captureCountdown > 0 && (
                      <div className="visitor-camera-countdown">
                        <span>{captureCountdown}</span>
                        <strong>{t.getReady}</strong>
                      </div>
                    )}

                    {photo && (
                      <img
                        className="support-us-live-camera__photo"
                        src={photo}
                        alt={t.image}
                      />
                    )}
                  </div>

                  {cameraError && (
                    <div className="support-us-camera-error">
                      <WarningCircle size={18} /> {cameraError}
                    </div>
                  )}

                  <canvas ref={canvasRef} hidden />

                  <div className="support-us-camera-actions">
                    {!photo && !cameraActive && (
                      <button
                        type="button"
                        className="btn btn--primary"
                        onClick={startCamera}
                      >
                        <Camera size={18} /> {t.openCamera}
                      </button>
                    )}

                    {!photo && cameraActive && (
                      <button
                        type="button"
                        className="btn btn--primary"
                        onClick={startCaptureCountdown}
                        disabled={captureCountdown > 0}
                      >
                        <Camera size={18} />
                        {captureCountdown > 0
                          ? `${t.capturingIn} ${captureCountdown}`
                          : t.capturePhoto}
                      </button>
                    )}

                    {photo && (
                      <button
                        type="button"
                        className="btn btn--ghost"
                        onClick={retakePhoto}
                      >
                        {t.retakePhoto}
                      </button>
                    )}
                  </div>
                </div>

                <div className="form-group form-group--full">
                  <div className="support-us-disclaimer">
                    <WarningCircle size={20} />
                    <p>{t.disclaimer}</p>
                  </div>
                </div>
              </div>

              <div className="submit-ticket-actions">
                <button
                  type="button"
                  className="btn btn--ghost"
                  onClick={() => navigate('/')}
                >
                  {t.backHome}
                </button>

                <button
                  type="submit"
                  className="btn btn--primary"
                  disabled={!canSubmit}
                >
                  <PaperPlaneTilt size={16} /> {t.submit}
                </button>
              </div>
            </form>
          ) : (
            <div className="kiosk-success-state">
              <CheckCircle size={58} />
              <h1>{t.successTitle}</h1>
              <p>{t.successDesc}</p>
            </div>
          )}
        </section>
      </div>
    </main>
  )
}